import React, { useCallback } from 'react';
import { Button, Input } from 'antd';
import { Wand2, Minimize2, Maximize2, ShieldCheck, Sparkles } from 'lucide-react';
import { useRightPanelContext, modeDescriptions } from '../RightPanelContext';
import { PROCESSING_MODE, ACTION_TYPE, LENGTH_OPTION, TONE_OPTION } from '../../constants';
import { post } from '../../../../../axios/axios';
import { ChipGroup } from '../shared/ChipGroup';
import { ResultCard } from '../shared/ResultCard';
import { PreviewActions } from '../shared/PreviewActions';
import styles from '../../../styles/index.module.scss';

const { TextArea } = Input;

const MODE_OPTIONS = [
    { value: PROCESSING_MODE.POLISH, label: '润色', icon: <Wand2 size={15} /> },
    { value: PROCESSING_MODE.SHRINK, label: '缩写', icon: <Minimize2 size={15} /> },
    { value: PROCESSING_MODE.EXPAND, label: '扩写', icon: <Maximize2 size={15} /> },
    { value: PROCESSING_MODE.REDUCE_DUPLICATE, label: '降重', icon: <ShieldCheck size={15} /> },
];

const TONE_OPTIONS = [
    { value: TONE_OPTION.ACADEMIC, label: '学术' },
    { value: TONE_OPTION.FORMAL, label: '正式' },
    { value: TONE_OPTION.PERSUASIVE, label: '有说服力' },
    { value: TONE_OPTION.WRITTEN, label: '叙述性' },
];

const API_BY_MODE = {
    [PROCESSING_MODE.POLISH]: '/ai/ai/paper/editor/v2/polish',
    [PROCESSING_MODE.SHRINK]: '/ai/ai/paper/editor/v2/summarize',
    [PROCESSING_MODE.EXPAND]: '/ai/ai/paper/editor/v2/expand',
    [PROCESSING_MODE.REDUCE_DUPLICATE]: '/ai/ai/paper/editor/v2/reduce-duplicate',
};

const isCanceled = (error) =>
    error?.name === 'AbortError' ||
    error?.name === 'CanceledError' ||
    error?.code === 'ERR_CANCELED' ||
    error?.message?.includes('canceled');

/**
 * 选中文字面板：润色 / 缩写 / 扩写 / 降重
 */
const SelectionPanel = () => {
    const {
        editor,
        commonState,
        textState,
        documentState,
        calculateTargetLength,
        formatLengthDisplay,
        handleCancelPreview,
        handleCopyResult,
        handleReplaceContent,
        handleReset,
    } = useRightPanelContext();

    const originalLength = textState.selectedText
        ? textState.selectedText.replace(/\s/g, '').length
        : 0;

    const handleGenerate = useCallback(async () => {
        if (!editor || commonState.generating) return;

        if (commonState.showPreview) {
            commonState.showPreview = false;
            textState.previewResult = '';
            commonState.previewMode = '';
        }

        let instruction = textState.customInstruction.trim();
        if (!instruction) {
            if (originalLength === 0) return;
            const targetLength = calculateTargetLength(
                originalLength,
                textState.processingMode,
                textState.lengthOption,
            );

            let lengthInstruction = '';
            if (textState.lengthOption === LENGTH_OPTION.ORIGINAL) {
                lengthInstruction = '保持原文篇幅不变';
            } else if (textState.processingMode === PROCESSING_MODE.SHRINK) {
                lengthInstruction = `将内容缩写到约${targetLength}字，保留核心要点`;
            } else if (textState.processingMode === PROCESSING_MODE.EXPAND) {
                lengthInstruction =
                    textState.lengthOption === LENGTH_OPTION.EXPAND
                        ? `将内容扩写到${targetLength}字以上，增加细节和论述`
                        : `将内容调整到约${targetLength}字`;
            } else if (textState.lengthOption === LENGTH_OPTION.SHRINK) {
                lengthInstruction = `将内容调整到约${targetLength}字，保留核心要点`;
            } else if (textState.lengthOption === LENGTH_OPTION.NORMAL) {
                lengthInstruction = `保持常规篇幅（约${targetLength}字）`;
            } else {
                lengthInstruction = `将内容扩写到${targetLength}字以上，增加细节和论述`;
            }

            const toneMap = {
                [TONE_OPTION.ACADEMIC]: '使用学术化的表达方式',
                [TONE_OPTION.PERSUASIVE]: '使用有说服力的论述风格',
                [TONE_OPTION.FORMAL]: '使用正式得体的语气',
                [TONE_OPTION.WRITTEN]: '使用书面化的叙述表达',
            };
            instruction = `${lengthInstruction}，${toneMap[textState.toneOption]}。`;
        }

        const contentBlockId =
            textState.selectedBlockIds.length > 0 ? textState.selectedBlockIds[0] : null;
        const { from, to } = textState.selectedRange;
        if (from === null || to === null) return;
        const plainText = editor.state.doc.textBetween(from, to, ' ');
        if (!plainText.trim()) return;

        commonState.generating = true;
        commonState.generationProgress = 20;
        commonState.generationStatus = '正在分析上下文…';

        try {
            const response = await post(
                API_BY_MODE[textState.processingMode] || API_BY_MODE[PROCESSING_MODE.POLISH],
                {
                    selectedContent: plainText,
                    userRequirement: instruction,
                    contentBlockId,
                },
                { params: { orderNo: documentState.orderNo } },
            );
            commonState.generationProgress = 100;
            commonState.generationStatus = '';
            commonState.generating = false;

            const resultContent = response?.data;
            if (resultContent) {
                textState.previewResult = resultContent;
                commonState.showPreview = true;
                commonState.previewMode = textState.processingMode;
            }
        } catch (error) {
            if (isCanceled(error)) return;
            commonState.generating = false;
            commonState.generationStatus = '生成失败，请重试';
            console.error('接口调用失败:', error);
        }
    }, [editor, commonState, textState, documentState, calculateTargetLength, originalLength]);

    const setMode = (mode) => {
        textState.processingMode = mode;
        textState.modeDescription = modeDescriptions[mode];
        if (mode === PROCESSING_MODE.SHRINK) {
            textState.currentAction = ACTION_TYPE.SHRINK;
            textState.lengthOption = LENGTH_OPTION.SHRINK;
        } else if (mode === PROCESSING_MODE.EXPAND) {
            textState.currentAction = ACTION_TYPE.EXPAND;
            textState.lengthOption = LENGTH_OPTION.EXPAND;
        } else if (mode === PROCESSING_MODE.REDUCE_DUPLICATE) {
            textState.currentAction = ACTION_TYPE.REDUCE_DUPLICATE;
        } else {
            textState.currentAction = '';
        }
    };

    const lengthOptions = [
        LENGTH_OPTION.ORIGINAL,
        LENGTH_OPTION.SHRINK,
        LENGTH_OPTION.NORMAL,
        LENGTH_OPTION.EXPAND,
    ].map((opt) => ({
        value: opt,
        label: { original: '原文', shrink: '精简', normal: '适中', expand: '详尽' }[opt],
        hint: formatLengthDisplay(
            calculateTargetLength(originalLength, textState.processingMode, opt),
            opt,
            originalLength,
        ),
    }));

    const excerpt =
        textState.selectedText.length > 90
            ? `${textState.selectedText.slice(0, 90)}…`
            : textState.selectedText;

    if (commonState.showPreview && textState.previewResult) {
        return (
            <div className={styles.panelSection}>
                <ResultCard
                    title="生成结果"
                    meta={`${textState.previewResult.replace(/\s/g, '').length} 字`}
                    actions={
                        <PreviewActions
                            onCancel={handleCancelPreview}
                            onCopy={handleCopyResult}
                            onRegenerate={handleGenerate}
                            onApply={handleReplaceContent}
                            generating={commonState.generating}
                            applyText="替换选中内容"
                        />
                    }
                >
                    <div className={styles.resultText}>{textState.previewResult}</div>
                </ResultCard>
            </div>
        );
    }

    return (
        <div className={styles.panelSection}>
            <div className={styles.quoteCard}>
                <div className={styles.quoteCardLabel}>已选中 {originalLength} 字</div>
                <div className={styles.quoteCardText}>{excerpt}</div>
            </div>

            <div className={styles.sectionLabel}>处理方式</div>
            <ChipGroup
                options={MODE_OPTIONS}
                value={textState.processingMode}
                onChange={setMode}
                disabled={commonState.generating}
                size="large"
            />
            <div className={styles.presetDesc}>{textState.modeDescription}</div>

            <div className={styles.sectionLabel}>目标篇幅</div>
            <ChipGroup
                options={lengthOptions}
                value={textState.lengthOption}
                onChange={(v) => {
                    textState.lengthOption = v;
                }}
                disabled={commonState.generating}
            />

            <div className={styles.sectionLabel}>语气风格</div>
            <ChipGroup
                options={TONE_OPTIONS}
                value={textState.toneOption}
                onChange={(v) => {
                    textState.toneOption = v;
                }}
                disabled={commonState.generating}
            />

            <div className={styles.sectionLabel}>额外要求（可选）</div>
            <TextArea
                value={textState.customInstruction}
                onChange={(e) => {
                    textState.customInstruction = e.target.value;
                }}
                placeholder="例如：保留文中的引用数据，并使用更委婉的语气…"
                autoSize={{ minRows: 2, maxRows: 5 }}
                disabled={commonState.generating}
                className={styles.instructionInput}
            />

            {commonState.generationStatus && (
                <div className={styles.generationStatus}>{commonState.generationStatus}</div>
            )}

            <Button
                type="primary"
                block
                size="large"
                icon={<Sparkles size={16} />}
                onClick={handleGenerate}
                loading={commonState.generating}
                disabled={originalLength === 0}
                className={styles.primaryAction}
            >
                {commonState.generating ? 'AI 处理中…' : '开始生成'}
            </Button>
            <Button block type="text" onClick={handleReset} disabled={commonState.generating}>
                取消
            </Button>
        </div>
    );
};

export default SelectionPanel;
